import React, { useEffect, useState, useCallback } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './contexts/AuthContext';
import { getMe } from './services/api';
import DashboardPage from './pages/DashboardPage';
import ReportsPage from './pages/ReportsPage';
import ReportWizard from './pages/ReportWizard';
import AppointmentsPage from './pages/AppointmentsPage';
import LeadsPage from './pages/LeadsPage';
import InvoicesPage from './pages/InvoicesPage';
import ClientsPage from './pages/ClientsPage';
import AdminPage from './pages/AdminPage';
import LoginPage from './pages/LoginPage';
import LegalPage from './pages/LegalPage';
import MobileUploadPage from './pages/MobileUploadPage';
import ClientReportPage from './pages/ClientReportPage';
import AppointmentConfirmPage from './pages/AppointmentConfirmPage';
import OfflineBanner from './components/OfflineBanner';

function getInitials(user) {
  if (user?.full_name) {
    return user.full_name
      .split(' ')
      .map((w) => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }
  return user?.email?.[0]?.toUpperCase() || '?';
}

function App() {
  const { t, i18n } = useTranslation();
  const { session, loading: authLoading, signOut } = useAuth();
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on navigation
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!session) {
      setCurrentUser(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    getMe()
      .then(setCurrentUser)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [session]);

  const toggleLang = () => {
    const next = i18n.language?.startsWith('fr') ? 'en' : 'fr';
    i18n.changeLanguage(next);
  };

  // The phone that scanned a QR code has no session and must never be sent to
  // the login screen: its authorisation is the signed token in the URL.
  if (location.pathname.startsWith('/upload/')) {
    return (
      <Routes>
        <Route path="/upload/:token" element={<MobileUploadPage />} />
      </Routes>
    );
  }

  // A report's client link: its recipient has no account — the unique token
  // in the URL is the access — so this never goes through login either.
  if (location.pathname.startsWith('/r/')) {
    return (
      <Routes>
        <Route path="/r/:token" element={<ClientReportPage />} />
      </Routes>
    );
  }

  // Appointment confirmation: same logic — the client confirms from the link
  // in their e-mail, with no account.
  if (location.pathname.startsWith('/confirm/')) {
    return (
      <Routes>
        <Route path="/confirm/:token" element={<AppointmentConfirmPage />} />
      </Routes>
    );
  }

  // Public legal pages render standalone, regardless of auth or loading state.
  if (location.pathname === '/privacy' || location.pathname === '/terms') {
    return (
      <Routes>
        <Route path="/privacy" element={<LegalPage doc="privacy" />} />
        <Route path="/terms" element={<LegalPage doc="terms" />} />
      </Routes>
    );
  }

  if (authLoading || loading) {
    return <p style={{ textAlign: 'center', paddingTop: 60 }}>{t('auth.loading')}</p>;
  }

  if (!session) {
    // Show connectivity on the login screen too: signing in needs the network.
    return (
      <>
        <OfflineBanner />
        <LoginPage />
      </>
    );
  }

  const adminIcon = (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
    </svg>
  );

  return (
    <div className="app">
      <OfflineBanner />
      <nav className="navbar">
        <Link to="/" className="navbar-brand">ExpertisR</Link>

        {/* Desktop nav */}
        <div className="navbar-links navbar-desktop">
          <Link to="/leads">{t('nav.leads')}</Link>
          <Link to="/appointments">{t('nav.appointments')}</Link>
          <Link to="/reports">{t('nav.reports')}</Link>
          <Link to="/clients">{t('nav.clients')}</Link>
          <Link to="/invoices">{t('nav.invoices')}</Link>
          <Link to="/reports/new">{t('nav.newReport')}</Link>
          <button className="lang-switch" onClick={toggleLang}>
            {i18n.language?.startsWith('fr') ? 'EN' : 'FR'}
          </button>
          {currentUser?.role === 'admin' && (
            <Link to="/admin" className="navbar-admin-link" title={t('nav.admin')}>
              {adminIcon}
            </Link>
          )}
          <div className="navbar-user">
            <div className="navbar-user-avatar">{getInitials(currentUser)}</div>
            <span className="navbar-user-name">
              {currentUser?.full_name || currentUser?.email}
            </span>
          </div>
          <button className="btn btn-secondary" onClick={signOut} style={{ marginLeft: 8 }}>
            {t('nav.logout')}
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          className="navbar-hamburger"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
        >
          <span /><span /><span />
        </button>
      </nav>

      {/* Mobile slide-in menu */}
      {menuOpen && <div className="navbar-overlay" onClick={() => setMenuOpen(false)} />}
      <div className={`navbar-mobile-menu ${menuOpen ? 'open' : ''}`}>
        <div className="navbar-mobile-user">
          <div className="navbar-user-avatar">{getInitials(currentUser)}</div>
          <span>{currentUser?.full_name || currentUser?.email}</span>
        </div>
        <nav className="navbar-mobile-nav">
          <Link to="/">{t('dashboard.title')}</Link>
          <Link to="/leads">{t('nav.leads')}</Link>
          <Link to="/appointments">{t('nav.appointments')}</Link>
          <Link to="/reports">{t('nav.reports')}</Link>
          <Link to="/clients">{t('nav.clients')}</Link>
          <Link to="/invoices">{t('nav.invoices')}</Link>
          <Link to="/reports/new">{t('nav.newReport')}</Link>
          {currentUser?.role === 'admin' && (
            <Link to="/admin">{adminIcon} {t('nav.admin')}</Link>
          )}
        </nav>
        <div className="navbar-mobile-footer">
          <button className="lang-switch" onClick={toggleLang}>
            {i18n.language?.startsWith('fr') ? 'EN' : 'FR'}
          </button>
          <button className="btn btn-secondary" onClick={signOut}>
            {t('nav.logout')}
          </button>
        </div>
      </div>

      <main className="container" style={{ paddingTop: 28, paddingBottom: 48 }}>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/leads" element={<LeadsPage />} />
          <Route path="/appointments" element={<AppointmentsPage />} />
          <Route path="/reports" element={<ReportsPage currentUser={currentUser} />} />
          <Route path="/invoices" element={<InvoicesPage />} />
          <Route path="/clients" element={<ClientsPage />} />
          <Route path="/reports/new" element={<ReportWizard />} />
          <Route path="/reports/:id" element={<ReportWizard />} />
          {currentUser?.role === 'admin' && (
            <Route path="/admin" element={<AdminPage />} />
          )}
        </Routes>
      </main>

      <footer className="app-footer">
        <Link to="/privacy">{t('legal.privacyLink')}</Link>
        <span className="app-footer-sep">·</span>
        <Link to="/terms">{t('legal.termsLink')}</Link>
      </footer>
    </div>
  );
}

export default App;
